// Layer 3 (Transformations): the Ops supervisor landing (polish.P13 #700,
// locked comp `landing-ops-supervisor.html` C, finished with A's 4 px shift
// rule and B's chip rules). Three strip-as-tabs cells (Before handover ·
// Equipment · Shift compliance) beside the Daily meeting's carried-in 3Ws, a
// flat band, and one panel row per view. Every figure reads the role seed, the
// Advisory transforms, the meeting register or the readings at the cursor;
// every count here is derived, never typed (adjudication 9).

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SampleAdherence } from '@/versions/v1/schema/unit-operation';
import type { Alarm, Case } from '@/versions/v1/schema/case';
import type { RoleTable } from '@/versions/v1/schema/plant-role';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { parseSampleAdherence } from '@/versions/v1/shared/StateChip';
import { ROLE_KPIS, ZN_PH_SETPOINT } from '@/versions/v1/shared/bunker-hill-seed';
import { RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { CONTROL_PLAN_ZN_ROWS } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { activeCrewForShift, BUNKER_HILL_PERSONS, crewLabelById, previousCrewForShift } from '@/versions/v1/shared/bunker-hill-org-seed';
import { TRAINING_ROSTER } from '@/versions/v1/modules/documents/seed';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { valueAt } from '@/versions/v1/shared/series.derive';
import { addMinutes, diffMinutes, formatCaseClock, previousShiftWindow, shiftOf } from '@/versions/v1/shared/clock.derive';
import {
  advisoryCasesForPanel,
  alarmsWithSession,
  assigneeLabel,
  casesWithSession,
  dcsAlarmsForPanel,
  sampleRequestRows,
  severityChip,
  zeroTouchAlarmResponses,
  type SampleRequestRow,
} from '@/versions/v1/modules/advisory/Scene.transform';
import {
  actionPriorityLabel,
  recordsCompleteFromChecks,
  sampleAdherenceLegend,
  sampleTrackingTableWithFooter,
  samplesOnTimeFromGrid,
  shiftComplianceChecks,
  shiftComplianceMeter,
  shiftGridCaseId,
  tablesForComplianceTab,
  type SampleLegendEntry,
} from '@/versions/v1/modules/compliance/Scene.transform';
import { handoverForShift, last12hVsPlan } from '@/versions/v1/modules/meeting/Scene.transform';
import { feedDesignStph } from './SuptCostsIssuesPlan.transform';
import { opsSupervisorTaskText } from './OperatorLanding.transform';
import { roleTable } from './RoleLanding.transform';
import type { GmMiniItem, LandingStripCell } from './GmLeadBand.transform';

export type OpsTabId = 'handover' | 'equipment' | 'compliance';

const ROLE = 'ops-supervisor';

const hm = (stamp: string): string => stamp.split('T')[1] ?? stamp;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const fmtT = (n: number) => Math.round(n).toLocaleString('en-US');

/** The closed honest state for a figure the seed does not carry (DESIGN.md § Decided). */
const UNMEASURED = 'unmeasured';

/** A seed table with no rows stands in for a missing one: every view renders
 *  at every cursor, and nothing on a render path throws (P13 round 1). */
function emptyTable(tabId: string, caption: string): RoleTable {
  return { roleId: ROLE, tabId, caption, columns: [], rows: [] };
}

function seedTable(tabId: string, captionStart: string): RoleTable {
  return roleTable(ROLE, captionStart, tabId) ?? emptyTable(tabId, captionStart);
}

function seedKpi(id: string) {
  return ROLE_KPIS.find((row) => row.id === id);
}

const minutesOf = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ---- The shift clock ------------------------------------------------------------

export type OpsShiftClock = {
  now: string;
  start: string;
  end: string;
  kind: 'day' | 'night';
  elapsedHours: number;
  lengthHours: number;
  crewLabel: string;
};

export function opsShiftClock(state: SessionState): OpsShiftClock {
  const shift = shiftOf(state.cursor);
  const crew = activeCrewForShift(state.cursor, shift.kind);
  return {
    now: hm(state.cursor),
    start: hm(shift.start),
    end: hm(shift.end),
    kind: shift.kind,
    elapsedHours: diffMinutes(state.cursor, shift.start) / 60,
    lengthHours: diffMinutes(shift.end, shift.start) / 60,
    crewLabel: crewLabelById(crew.crewId).replace(/ Crew$/, ' crew'),
  };
}

/** The page subtitle: the running shift's window, then the landing's three reads. */
export function opsSubtitle(state: SessionState): string {
  const clock = opsShiftClock(state);
  return `${clock.start}–${clock.end} · handover, equipment and shift discipline`;
}

/** The Ops supervisor seat's holder on the crew now on shift (`D. Lee` on C
 *  crew, `M. Navarro` on B). The roster seats a supervisor on B and C only, so
 *  on an A- or D-crew shift the seat has no name: null, and the landing names
 *  the crew instead (P13 round 1 MUST-FIX 1). */
export function opsSupervisorOnShift(state: SessionState): string | null {
  const shift = shiftOf(state.cursor);
  const crewId = activeCrewForShift(state.cursor, shift.kind).crewId;
  const member = TRAINING_ROSTER.find((row) => row.seatId === 'seat-ops-supervisor' && row.crewId === crewId);
  return BUNKER_HILL_PERSONS.find((row) => row.id === member?.personId)?.name ?? null;
}

// ---- Times on the running shift's clock ----------------------------------------------

/** Minutes from the running shift's start to a seed due time. A dated cell
 *  (`08 Jun 11:45`) is placed on the calendar; a bare `HH:MM` (grid slots,
 *  checks) is placed on the running shift's own clock, so a night shift that
 *  wraps midnight compares as times, never as strings (P13 round 1 item 5). */
function shiftOffset(state: SessionState, due: string): number {
  const shift = shiftOf(state.cursor);
  const dated = due.match(/^(\d{1,2}) ([A-Z][a-z]{2}) (\d{2}:\d{2})$/);
  if (dated) {
    const month = String(MONTHS.indexOf(dated[2]!) + 1).padStart(2, '0');
    const year = state.cursor.slice(0, 4);
    return diffMinutes(`${year}-${month}-${dated[1]!.padStart(2, '0')}T${dated[3]}`, shift.start);
  }
  const clock = due.match(/(\d{2}:\d{2})/)?.[1];
  if (!clock) return Number.NaN;
  return (minutesOf(clock) - minutesOf(hm(shift.start)) + 24 * 60) % (24 * 60);
}

/** The shift's round cadence, read from the rounds grid (its slot spacing; 2 h). */
function roundCadenceMinutes(): number {
  const slots = roundsGrid().columns.slice(1);
  const step = slots.length > 1 ? minutesOf(slots[1]!) - minutesOf(slots[0]!) : 0;
  return step > 0 ? step : 120;
}

export type OpsNextRound = {
  /** Minutes from shift start to the next round boundary after the cursor, capped at shift end. */
  offset: number;
  at: string;
  /** True when the next boundary is the shift end itself (the handover). */
  isHandover: boolean;
};

/** The next round boundary strictly after the cursor: the next queue window
 *  closes there and the shift rule's tick sits on it, so the tick never sits
 *  behind the elapsed fill (P13 round 1 item 5). */
export function opsNextRound(state: SessionState): OpsNextRound {
  const clock = opsShiftClock(state);
  const shift = shiftOf(state.cursor);
  const elapsed = diffMinutes(state.cursor, shift.start);
  const cadence = roundCadenceMinutes();
  const length = clock.lengthHours * 60;
  const offset = Math.min(length, cadence * (Math.floor(elapsed / cadence) + 1));
  return { offset, at: hm(addMinutes(shift.start, offset)), isHandover: offset >= length };
}

// ---- Zn flotation pH against its one setpoint field --------------------------------

/** The AIC5002 setpoint at the cursor: the seed's `after` once the ZN-REC lime
 *  step (`ra-2`) is implemented, its `before` until then. */
export function znPhSetpointAt(state: SessionState): { value: number; since?: string } {
  const step = RECOMMENDED_ACTIONS.find((row) => row.id === ZN_PH_SETPOINT.stepActionId);
  const at = step?.implementedAt;
  if (at && diffMinutes(state.cursor, at) >= 0) return { value: ZN_PH_SETPOINT.after, since: hm(at) };
  return { value: ZN_PH_SETPOINT.before };
}

// ---- The equipment board ----------------------------------------------------------

export type OpsUnitRow = {
  id: string;
  name: string;
  status: 'RUN' | 'WARN';
  param: string;
  /** The basis the parameter reads against, where the board carries one (`vs 75.0 stph design`). */
  comparison?: string;
  avail: string;
  power: string;
};

const ZN_ROUGHER_ROW = 'ops-r7';
const BALL_MILL_ROW = 'ops-r2';

/** The board's twelve units. The Zn rougher row reads AT-150 at the cursor
 *  against the one setpoint field and goes WARN outside the Control Plan's
 *  `cp-zn-ph` band (adjudication 1, as ruled: SP 11.0 after the 08:00 lime
 *  step); every other row is the role seed's. */
export function opsEquipmentRows(state: SessionState): OpsUnitRow[] {
  const table = seedTable('equipment', 'Equipment status board');
  return table.rows.map((row) => {
    const [name = '', status = '', param = '', avail = '', power = ''] = row.cells;
    const base: OpsUnitRow = { id: row.id, name, status: status === 'WARN' ? 'WARN' : 'RUN', param, avail, power };
    if (row.id === BALL_MILL_ROW) {
      return { ...base, comparison: `vs ${feedDesignStph(state).toFixed(1)} stph design` };
    }
    if (row.id !== ZN_ROUGHER_ROW) return base;
    const reading = readingByIdLookup(ZN_PH_SETPOINT.readingId);
    const ph = valueAt(ZN_PH_SETPOINT.readingId, state) ?? reading?.value;
    const band = CONTROL_PLAN_ZN_ROWS.find((cp) => cp.id === ZN_PH_SETPOINT.controlPlanRowId)?.specOverlay;
    // No reading or band at this cursor: the seed's own row stands (never a throw).
    if (ph === undefined || !band || band.lo === undefined || band.hi === undefined) return base;
    const sp = znPhSetpointAt(state);
    return {
      ...base,
      status: ph < band.lo || ph > band.hi ? 'WARN' : 'RUN',
      // `since` names the lime step, so the alarm's older SP=10.8 and this
      // SP 11.0 read as two moments, not two setpoints (item 8).
      param: `pH ${ph.toFixed(1)} · SP ${sp.value.toFixed(1)}${sp.since ? ` since ${sp.since}` : ''}`,
    };
  });
}

export function opsWarnUnits(state: SessionState): { warn: OpsUnitRow[]; total: number } {
  const rows = opsEquipmentRows(state);
  return { warn: rows.filter((row) => row.status === 'WARN'), total: rows.length };
}

// ---- Shift compliance: the grids, checks and actions --------------------------------

function complianceTable(captionStart: string): RoleTable {
  return (
    tablesForComplianceTab('shift').find((row) => row.caption.startsWith(captionStart)) ??
    emptyTable('shift-compliance', captionStart)
  );
}

function sampleGrid(): RoleTable {
  return complianceTable('Sample tracking');
}

function roundsGrid(): RoleTable {
  return complianceTable('Operator rounds');
}

function actionsTable(): RoleTable {
  return complianceTable('Outstanding actions');
}

/** Grid slots that have passed at the cursor (the slot start is before now, on the shift's clock). */
function pastSlots(grid: RoleTable, state: SessionState): string[] {
  const elapsed = opsShiftClock(state).elapsedHours * 60;
  return grid.columns.slice(1).filter((slot) => shiftOffset(state, slot) < elapsed && !isDueSlot(grid, slot));
}

function isDueSlot(grid: RoleTable, slot: string): boolean {
  const index = grid.columns.indexOf(slot);
  return grid.rows.some((row) => parseSampleAdherence(row.cells[index] ?? '') === 'DUE');
}

/** The next due slot on a grid — the first column any row reads DUE. */
function dueSlot(grid: RoleTable): { slot: string; rows: RoleTable['rows'] } | null {
  for (const slot of grid.columns.slice(1)) {
    const index = grid.columns.indexOf(slot);
    const rows = grid.rows.filter((row) => parseSampleAdherence(row.cells[index] ?? '') === 'DUE');
    if (rows.length > 0) return { slot, rows };
  }
  return null;
}

export type OpsRoundsSummary = { onTime: number; total: number; late: number; pending: number };

/** Operator rounds reconstructed from the rounds grid's past slots, as Samples
 *  on time is from the sample grid (adjudication 4; the typed 15/18 goes). */
export function roundsFromGrid(state: SessionState): OpsRoundsSummary {
  const grid = roundsGrid();
  const slots = pastSlots(grid, state);
  let onTime = 0;
  let total = 0;
  let late = 0;
  let pending = 0;
  for (const row of grid.rows) {
    for (const slot of slots) {
      const cell = parseSampleAdherence(row.cells[grid.columns.indexOf(slot)] ?? '');
      if (cell === null || cell === 'N/A') continue;
      total += 1;
      if (cell === 'REG') onTime += 1;
      if (cell === 'LATE') late += 1;
      if (cell === 'PENDING') pending += 1;
    }
  }
  return { onTime, total, late, pending };
}

export type OpsMissedSample = { stream: string; slot: string; caseId?: string };

/** Every MISS cell on the sample grid, with the case it opens (S11). */
export function missedSamples(): OpsMissedSample[] {
  const grid = sampleGrid();
  return grid.rows.flatMap((row) =>
    grid.columns.slice(1).flatMap((slot, i) => {
      const cell = row.cells[i + 1] ?? '';
      if (parseSampleAdherence(cell) !== 'MISS') return [];
      return [{ stream: row.cells[0] ?? '', slot, caseId: shiftGridCaseId(row.id, slot, cell) }];
    }),
  );
}

/** The seven-state grid cell as the chip reads it, and the case a MISS opens. */
export type OpsGridCell = { state: SampleAdherence | null; text: string; caseId?: string };

export type OpsGrid = {
  caption: string;
  /** The card head's meta (`11 streams · now 11:30`, `every 2 hours`), counted here, not in the UI. */
  meta: string;
  columns: readonly string[];
  /** The slot the next round is due in, drawn in the info tone (comp 12:00). */
  dueSlot?: string;
  rows: { id: string; label: string; cells: OpsGridCell[] }[];
  /** Per-slot compliance under the sample grid (`8/9 · 89%`); the due slot reads its chip. */
  footer?: { text: string; state: SampleAdherence | null }[];
};

function gridRows(table: RoleTable): OpsGrid['rows'] {
  return table.rows.map((row) => ({
    id: row.id,
    label: row.cells[0] ?? '',
    cells: row.cells.slice(1).map((text, i) => ({
      state: parseSampleAdherence(text),
      text,
      caseId: shiftGridCaseId(row.id, table.columns[i + 1] ?? '', text),
    })),
  }));
}

export function opsSampleGrid(state: SessionState): OpsGrid {
  const seed = sampleGrid();
  const footerRow = seed.rows.length > 0 ? sampleTrackingTableWithFooter(seed).rows.at(-1) : undefined;
  return {
    caption: 'Sample tracking',
    meta: `${plural(seed.rows.length, 'stream')} · now ${opsShiftClock(state).now}`,
    columns: seed.columns,
    dueSlot: dueSlot(seed)?.slot,
    rows: gridRows(seed),
    footer: footerRow?.cells.slice(1).map((cell) => ({
      text: cell.replace(/^(\d+\/\d+)\s+(\d+%)$/, '$1 · $2'),
      state: cell === 'DUE' ? 'DUE' : null,
    })),
  };
}

export function opsRoundsGrid(): OpsGrid {
  const seed = roundsGrid();
  const cadence = roundCadenceMinutes();
  return {
    caption: 'Operator rounds',
    meta: cadence % 60 === 0 ? `every ${plural(cadence / 60, 'hour')}` : `every ${cadence} minutes`,
    columns: seed.columns,
    dueSlot: dueSlot(seed)?.slot,
    rows: gridRows(seed),
  };
}

export function opsSampleLegend(): readonly SampleLegendEntry[] {
  return sampleAdherenceLegend();
}

export type OpsAction = {
  id: string;
  priority: string;
  /** `P1` … `P3`, the tier the queue prints. */
  tier: string;
  tone: 'bad' | 'warn' | 'neutral';
  text: string;
  who: string;
  /** `HH:MM`, as the card prints it. */
  due: string;
  /** The seed's due cell (`08 Jun 11:45`), placed on the calendar by `shiftOffset`. */
  dueCell: string;
};

const PRIORITY_TONE: Record<string, OpsAction['tone']> = { URGENT: 'bad', HIGH: 'warn' };

export function opsActions(): OpsAction[] {
  return actionsTable().rows.map((row) => {
    const [priority = '', text = '', who = '', due = ''] = row.cells;
    const label = actionPriorityLabel(priority);
    return {
      id: row.id,
      priority: label,
      tier: label.split(' · ')[0] ?? label,
      tone: PRIORITY_TONE[priority] ?? 'neutral',
      text,
      who,
      due: due.split(' ').pop() ?? due,
      dueCell: due,
    };
  });
}

/** The Outstanding actions card: its rows and its head's meta. The strip's
 *  Before handover cell owns the P1 count, so the meta states only how many
 *  are open (say it once — P13 round 1 MUST-FIX 4). */
export function opsActionsCard(): { meta: string; rows: OpsAction[] } {
  const rows = opsActions();
  return { meta: `${rows.length} open`, rows };
}

export function opsChecks() {
  return shiftComplianceChecks();
}

/** The compliance view's band: samples, rounds and records, each with its read. */
export function opsComplianceBand(state: SessionState): GmMiniItem[] {
  const samples = samplesOnTimeFromGrid(sampleGrid());
  const rounds = roundsFromGrid(state);
  const records = recordsCompleteFromChecks(shiftComplianceChecks());
  const due = shiftComplianceChecks().filter((check) => check.state === 'DUE');
  const roundParts = [
    rounds.late > 0 ? `${rounds.late} late` : null,
    rounds.pending > 0 ? `${rounds.pending} LIMS pending` : null,
  ].filter(Boolean) as string[];
  return [
    {
      id: 'ops-samples',
      label: 'Samples on time',
      value: String(samples.onTime),
      unit: `/ ${samples.total}`,
      tone: samples.onTime === samples.total ? 'ok' : 'warn',
      captionLead: String(samples.limsPending),
      caption: ' LIMS pending',
    },
    {
      id: 'ops-rounds',
      label: 'Operator rounds',
      value: String(rounds.onTime),
      unit: `/ ${rounds.total}`,
      tone: rounds.onTime === rounds.total ? 'ok' : 'warn',
      captionLead: roundParts[0] ?? 'on time',
      caption: roundParts.length > 1 ? ` · ${roundParts.slice(1).join(' · ')}` : '',
    },
    {
      id: 'ops-records',
      label: 'Records complete',
      value: String(records.complete),
      unit: `/ ${records.total}`,
      tone: records.complete === records.total ? 'ok' : 'warn',
      captionLead: records.sub.split(' ')[0] ?? '',
      caption: ` ${records.sub.split(' ').slice(1).join(' ')}${due[0] ? ` · ${checkSubject(due[0].label)}` : ''}`,
    },
  ];
}

/** `pH meter calibration (Zn circuit) — due 12:00` → `pH meter, Zn circuit`. */
function checkSubject(label: string): string {
  const head = label.split(' — ')[0] ?? label;
  const match = head.match(/^(.*?)\s+calibration\s+\((.*)\)$/i);
  return match ? `${match[1]}, ${match[2]}` : head;
}

// ---- Before handover: one queue of what the crew closes before shift end -----------

export type OpsQueueTag =
  | { kind: 'priority'; label: string; tone: OpsAction['tone'] }
  | { kind: 'chip'; state: SampleAdherence }
  | { kind: 'task'; status: 'in-progress' | 'open' };

export type OpsQueueItem = {
  id: string;
  /** `HH:MM`, or null for a task with no due time. */
  due: string | null;
  /** Minutes from shift start to the due time (null when undated); the queue sorts and groups on it. */
  offset: number | null;
  /** The next due item is in the state-blocked tone when it is an urgent action
   *  (comp `.tm.soon`); so is every overdue item. */
  soon: boolean;
  tag: OpsQueueTag;
  text: string;
  title?: string;
  owner?: string;
  source: string;
};

export type OpsQueueGroup = {
  id: 'overdue' | 'next' | 'later' | 'undated' | 'handover';
  label: string;
  items: OpsQueueItem[];
};

export type OpsQueue = { groups: OpsQueueGroup[]; total: number; doneTasks: number };

const HANDOVER_REPORT = /handover report/i;
const SOURCE_RANK = ['action', 'samples', 'rounds', 'check'];

function taskRows(state: SessionState) {
  return seedTable('equipment', 'Shift task list').rows.map((row) => {
    const [glyph = '', text = ''] = row.cells;
    const status: 'done' | 'in-progress' | 'open' = glyph === '✓' ? 'done' : glyph === '⟳' ? 'in-progress' : 'open';
    return { id: row.id, status, text: opsSupervisorTaskText(text, state) };
  });
}

/** The first window's label: `Next 30 minutes` up to an hour out, else `Before 12:00`. */
function nextWindowLabel(minutes: number, at: string): string {
  return minutes <= 60 ? `Next ${minutes} minutes` : `Before ${at}`;
}

/** Everything the crew must close before the shift ends, in due order with
 *  owners: the outstanding actions, the next sample and rounds slot, the due
 *  checks, the open shift tasks, and the handover report — the task and the
 *  check are one deliverable, so they merge into one row at shift end
 *  (adjudication 7). Times group on the shift's own clock at the cursor:
 *  overdue, up to the next round boundary, later this shift; an item due
 *  outside this shift is not this crew's to close (P13 round 1 item 5). */
export function opsQueue(state: SessionState): OpsQueue {
  const clock = opsShiftClock(state);
  const crew = clock.crewLabel;
  const elapsed = Math.round(clock.elapsedHours * 60);
  const length = clock.lengthHours * 60;
  const boundary = opsNextRound(state);
  const items: OpsQueueItem[] = [];

  for (const action of opsActions()) {
    items.push({
      id: action.id,
      due: action.due,
      offset: shiftOffset(state, action.dueCell),
      soon: false,
      tag: { kind: 'priority', label: action.tier, tone: action.tone },
      text: action.text,
      owner: action.who,
      source: 'action',
    });
  }
  const samples = dueSlot(sampleGrid());
  if (samples) {
    items.push({
      id: 'due-samples',
      due: samples.slot,
      offset: shiftOffset(state, samples.slot),
      soon: false,
      tag: { kind: 'chip', state: 'DUE' },
      text: `Sample round · ${plural(samples.rows.length, 'stream')}`,
      title: samples.rows.map((row) => row.cells[0]).join(', '),
      owner: crew,
      source: 'samples',
    });
  }
  const rounds = dueSlot(roundsGrid());
  if (rounds) {
    items.push({
      id: 'due-rounds',
      due: rounds.slot,
      offset: shiftOffset(state, rounds.slot),
      soon: false,
      tag: { kind: 'chip', state: 'DUE' },
      text: `Operator rounds · ${plural(rounds.rows.length, 'area')}`,
      title: rounds.rows.map((row) => row.cells[0]).join(', '),
      owner: crew,
      source: 'rounds',
    });
  }
  const checks = shiftComplianceChecks();
  for (const check of checks.filter((row) => row.state === 'DUE')) {
    const due = check.label.match(/due (\d{2}:\d{2})/)?.[1] ?? null;
    items.push({
      id: check.id,
      due,
      offset: due ? shiftOffset(state, due) : null,
      soon: false,
      tag: { kind: 'chip', state: 'DUE' },
      text: check.label.split(' — ')[0] ?? check.label,
      source: 'check',
    });
  }

  const tasks = taskRows(state);
  const handoverCheck = checks.find((row) => HANDOVER_REPORT.test(row.label));
  const undated = tasks.filter((row) => row.status !== 'done' && !HANDOVER_REPORT.test(row.text));
  const handoverTask = tasks.find((row) => row.status !== 'done' && HANDOVER_REPORT.test(row.text));

  // Due time first (as minutes on the shift's clock); at one time, actions
  // lead, then the sample round, the rounds and the checks, each in its seed
  // order (comp C's queue). An item due after this shift ends is dropped.
  const rank = (row: OpsQueueItem) => SOURCE_RANK.indexOf(row.source);
  const dated = items
    .filter((row) => row.offset !== null && Number.isFinite(row.offset) && row.offset < length)
    .map((row, index) => ({ row, index }))
    .sort((a, b) => a.row.offset! - b.row.offset! || rank(a.row) - rank(b.row) || a.index - b.index)
    .map(({ row }) => row);

  const overdue = dated.filter((row) => row.offset! < elapsed);
  const next = dated.filter((row) => row.offset! >= elapsed && row.offset! <= boundary.offset);
  const later = dated.filter((row) => row.offset! > boundary.offset);
  for (const row of overdue) row.soon = true;
  const first = next[0];
  if (first && first.tag.kind === 'priority' && first.tag.tone === 'bad') first.soon = true;

  const owner = opsSupervisorOnShift(state);
  const groups: OpsQueueGroup[] = [
    { id: 'overdue', label: 'Overdue', items: overdue },
    { id: 'next', label: nextWindowLabel(boundary.offset - elapsed, boundary.at), items: next },
    {
      id: 'later',
      label: clock.kind === 'night' ? 'Later tonight' : minutesOf(boundary.at) >= 12 * 60 ? 'This afternoon' : 'Later this shift',
      items: later,
    },
    {
      id: 'undated',
      label: 'This shift · no due time',
      items: undated.map((row) => ({
        id: row.id,
        due: null,
        offset: null,
        soon: false,
        tag: { kind: 'task', status: row.status === 'in-progress' ? 'in-progress' : 'open' },
        text: row.text,
        source: 'task',
      })),
    },
  ];
  if (handoverTask || handoverCheck) {
    groups.push({
      id: 'handover',
      label: 'At handover',
      items: [
        {
          id: 'handover-report',
          due: clock.end,
          offset: length,
          soon: false,
          tag: { kind: 'chip', state: handoverCheck?.state ?? 'UPCOMING' },
          text: handoverTask?.text ?? handoverCheck?.label.split(' — ')[0] ?? '',
          // No supervisor rostered on this crew: the crew owns the report.
          owner: owner ?? crew,
          source: handoverTask && handoverCheck ? 'task · check' : handoverTask ? 'task' : 'check',
        },
      ],
    });
  }
  const shown = groups.filter((group) => group.items.length > 0);
  return {
    groups: shown,
    total: shown.reduce((sum, group) => sum + group.items.length, 0),
    doneTasks: tasks.filter((row) => row.status === 'done').length,
  };
}

// ---- The lead band ------------------------------------------------------------------

/** The Before handover cell's comparison: the P1 actions still in the queue
 *  and when they fall due — the one place the P1 count is stated. */
function p1Lead(queue: OpsQueue): { lead: string; overdue: boolean } | null {
  const p1 = queue.groups
    .flatMap((group) => group.items.map((item) => ({ item, overdue: group.id === 'overdue' })))
    .filter(({ item }) => item.source === 'action' && item.tag.kind === 'priority' && item.tag.label === 'P1');
  if (p1.length === 0) return null;
  const noun = p1.length === 1 ? 'action' : 'actions';
  if (p1.every((row) => row.overdue)) return { lead: `${p1.length} P1 ${noun} overdue`, overdue: true };
  const by = p1.reduce((last, row) => (row.item.offset! > last.item.offset! ? row : last)).item.due;
  return { lead: `${p1.length} P1 ${noun} by ${by}`, overdue: false };
}

export function opsStripCells(state: SessionState): LandingStripCell<OpsTabId>[] {
  const clock = opsShiftClock(state);
  const queue = opsQueue(state);
  const p1 = p1Lead(queue);
  const oee = seedKpi('ops-oee');
  const oeeDelta = oee?.sub?.match(/^([+−-][\d.]+ pt)/)?.[1] ?? '';
  const units = opsWarnUnits(state);
  const compliance = shiftComplianceMeter();
  const misses = missedSamples().length;
  return [
    {
      id: 'handover',
      name: 'Before handover',
      figure: String(queue.total),
      figureContext: 'to close',
      comparisonLead: p1 ? p1.lead : `handover ${clock.end}`,
      comparisonRest: p1 ? ` · handover ${clock.end}` : '',
      actor: 'Human',
      actorLabel: clock.crewLabel,
    },
    {
      id: 'equipment',
      name: 'Equipment · OEE',
      figure: oee?.value ?? UNMEASURED,
      figureContext: 'last shift',
      comparisonLead: oeeDelta,
      comparisonRest: `${oeeDelta ? ' vs shift before · ' : ''}${units.warn.length} of ${units.total} units WARN`,
      actor: 'System',
      actorLabel: 'DCS',
    },
    {
      id: 'compliance',
      name: 'Shift compliance',
      figure: `${compliance.value.toFixed(1)}%`,
      comparisonLead: `vs ${compliance.target}% target`,
      comparisonRest: misses > 0 ? ` · ${plural(misses, 'missed sample')}` : '',
      actor: 'Human',
      actorLabel: clock.crewLabel,
    },
  ];
}

export type OpsCarriedCard = {
  open: number;
  fromCrew: string;
  toCrew: string;
  /** The carried 3Ws the supervisor on shift owns; null when the crew has no rostered supervisor. */
  owned: { count: number; owner: string } | null;
};

/** The onward card: the 3Ws the handover carries in from the crew that just
 *  came off, and how many the supervisor on shift owns (adjudication 5). The
 *  crew that came off comes from the shared `previousCrewForShift`, the same
 *  helper the Daily meeting's `handoverCrew` reads (#777). */
export function opsCarriedCard(state: SessionState): OpsCarriedCard {
  const carried = handoverForShift(state).carried;
  const owner = opsSupervisorOnShift(state);
  return {
    open: carried.length,
    fromCrew: crewLabelById(previousCrewForShift(state.cursor).crewId).replace(/ Crew$/, ' crew'),
    toCrew: opsShiftClock(state).crewLabel,
    owned: owner ? { count: carried.filter((row) => row.who === owner).length, owner } : null,
  };
}

// ---- The status band (Before handover) ----------------------------------------------

/** The last completed shift's milled tonnes against its budget (half the
 *  daily budget) and its design: the handover's own `last12hVsPlan()` row, a
 *  running shift never an actual (P13 adjudication 2). The Control room
 *  landing's first strip cell reads the same derivation (polish.P15). */
export function lastShiftTonnes(state: SessionState): {
  window: ReturnType<typeof previousShiftWindow>;
  actual: number;
  budget: number;
  design?: number;
} | null {
  const window = previousShiftWindow(state.cursor);
  const tonnes = last12hVsPlan(state).rows.find((row) => row.id === 'throughput');
  if (!tonnes) return null;
  const design = readingByIdLookup(window.kind === 'night' ? 'rd-kpi-milled-night' : 'rd-kpi-milled-day')?.design;
  return { window, actual: tonnes.actual, budget: tonnes.target, design };
}

export function lastShiftItem(state: SessionState): GmMiniItem {
  const window = previousShiftWindow(state.cursor);
  const label = `Last shift · ${window.kind} ${hm(window.start)}–${hm(window.end)}`;
  const read = lastShiftTonnes(state);
  if (!read) return { id: 'ops-last-shift', label, value: UNMEASURED, tone: 'neutral', caption: '' };
  const tonnes = { actual: read.actual, target: read.budget };
  const design = read.design;
  const gap = tonnes.actual - tonnes.target;
  return {
    id: 'ops-last-shift',
    label,
    value: fmtT(tonnes.actual),
    unit: 't milled',
    tone: gap >= 0 ? 'ok' : 'warn',
    captionLead: `vs budget ${fmtT(tonnes.target)} t`,
    captionDelta: { text: `${gap >= 0 ? '+' : '−'}${fmtT(Math.abs(gap))} t`, tone: gap >= 0 ? 'ok' : 'warn' },
    caption: design !== undefined ? ` · design ${fmtT(design)} t` : '',
  };
}

export function opsStatusBand(state: SessionState, advisory: AdvisorySessionState): GmMiniItem[] {
  const clock = opsShiftClock(state);
  const next = opsNextRound(state);
  const length = clock.lengthHours * 60;
  const elapsedPct = (clock.elapsedHours / clock.lengthHours) * 100;
  const tickPct = (next.offset / length) * 100;
  const alarms = alarmsWithSession(advisory, state);
  const dcs = dcsAlarmsForPanel(alarms).visible;
  const advisoryAlarms = alarms.filter((alarm) => alarm.origin === 'advisory');
  const unacked = dcs.filter((alarm) => alarm.state === 'active').length;
  const aside = next.isHandover ? `handover ${next.at}` : `next round ${next.at}`;
  return [
    lastShiftItem(state),
    {
      id: 'ops-shift-hours',
      label: 'Shift hours',
      value: clock.elapsedHours.toFixed(1),
      unit: `/ ${clock.lengthHours} h`,
      tone: 'ok',
      caption: `${Math.round(elapsedPct)}% elapsed`,
      // The tick is the next round boundary strictly after the cursor, so it
      // always sits ahead of the elapsed fill (item 5).
      rule: {
        filledPct: elapsedPct,
        tickPct,
        tickTitle: `${clock.start}–${clock.end} · ${aside}`,
        aside,
      },
    },
    {
      id: 'ops-alarms',
      label: 'Active alarms',
      value: String(alarms.length),
      tone: unacked > 0 ? 'warn' : 'ok',
      captionLead: `${unacked} unacked`,
      caption: ` · ${dcs.length - unacked} acked · ${plural(advisoryAlarms.length, 'case')}`,
    },
  ];
}

// ---- Abnormal now and the alarm panel ------------------------------------------------

export type OpsAlarmRow = {
  id: string;
  code: string;
  chip: { label: string; tone: VizTone };
  message: string;
  raised: string;
  active: boolean;
  high: boolean;
};

export type OpsZeroTouch = { alarmCode: string; caseId: string; action: string; routedTo: string; branch: string; controlMode?: string };

export type OpsCaseRow = {
  id: string;
  code: string;
  chip: { label: string; tone: VizTone };
  title: string;
  raised: string;
  state: string;
  confidence: string;
};

export type OpsAbnormal = {
  meta: string;
  alarms: OpsAlarmRow[];
  zeroTouch: OpsZeroTouch[];
  warnUnits: OpsUnitRow[];
  misses: OpsMissedSample[];
  sampleRequests: SampleRequestRow[];
  cases: OpsCaseRow[];
};

/** A figure and its unit never part at a line end (`80.4 °C`): the space
 *  between them becomes a no-break space (item 6, the orphaned `°C)`). */
function keepUnitsWithFigures(text: string): string {
  return text.replace(/(\d) (°C|%|kW|stph|t\/h|gpm|µm)/g, '$1\u00a0$2');
}

function alarmRow(alarm: Alarm, state: SessionState): OpsAlarmRow {
  const chip = severityChip(alarm);
  return {
    id: alarm.id,
    code: alarm.code,
    chip,
    message: keepUnitsWithFigures(alarm.message ?? ''),
    raised: formatCaseClock(alarm.raisedAt, state.cursor),
    active: alarm.state === 'active',
    high: alarm.priority === 'P1' || alarm.priority === 'P2',
  };
}

function caseRow(caseItem: Case, state: SessionState): OpsCaseRow {
  return {
    id: caseItem.id,
    code: caseItem.code ?? '',
    chip: severityChip(caseItem),
    title: caseItem.title,
    raised: formatCaseClock(caseItem.raisedAt, state.cursor),
    state: caseItem.state.replace(/-/g, ' '),
    confidence: caseItem.aiConfidencePct !== undefined ? `${caseItem.aiConfidencePct}%` : '—',
  };
}

/** What is abnormal on the plant now: the DCS alarms, the zero-touch response
 *  one of them spawned, the WARN units, the missed sample with its request,
 *  and the open AI cases. The caption reads the two counts as two sets
 *  (adjudication 3), each from its own transform. */
export function opsAbnormal(state: SessionState, advisory: AdvisorySessionState): OpsAbnormal {
  const alarms = alarmsWithSession(advisory, state);
  const cases = casesWithSession(state, advisory);
  const dcs = dcsAlarmsForPanel(alarms).visible;
  const open = advisoryCasesForPanel(cases);
  return {
    meta: `${plural(dcs.length, 'DCS alarm')} · ${plural(open.length, 'open case')}`,
    alarms: dcs.map((alarm) => alarmRow(alarm, state)),
    zeroTouch: zeroTouchAlarmResponses(alarms, cases).map(({ alarm, caseItem }) => ({
      alarmCode: alarm.code,
      caseId: caseItem.id,
      action: caseItem.title.split(': ').slice(1).join(': ') || caseItem.title,
      routedTo: assigneeLabel(caseItem.assignedTo).toLowerCase(),
      branch: caseItem.branch,
      controlMode: caseItem.controlModeCheck
        ? `${caseItem.controlModeCheck.tag} = ${caseItem.controlModeCheck.mode.toUpperCase()}`
        : undefined,
    })),
    warnUnits: opsWarnUnits(state).warn,
    misses: missedSamples(),
    sampleRequests: sampleRequestRows(state, advisory),
    cases: open.map((caseItem) => caseRow(caseItem, state)),
  };
}
